import { useMemo, useRef, useState } from 'react';
import type { CreateGuaranteedServiceRequest, PatchGuaranteedServiceRequest } from '@nadoutong/contracts';
import { SkuGroupPicker } from '../components/SkuGroupPicker.tsx';
import { GUARANTEED_CUSTOMERS_STALE_MS, useQuotaCustomers } from '../api/customerOwnership.ts';
import { useGuaranteedServiceList, useGuaranteedServiceTextSkus } from '../api/guaranteedService.ts';
import { useToast } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import { Button, ConfirmDialog, Dialog, FormField, Input, PageHeader, Textarea } from '../ui/index.ts';
import { SearchDropdown } from './quotas/SearchDropdown.tsx';
import { ConfigCards } from './guaranteed/ConfigCards.tsx';
import { EventsCard } from './guaranteed/EventsCard.tsx';
import { LegStatusCard } from './guaranteed/LegStatusCard.tsx';
import { ProbeSpendCard } from './guaranteed/ProbeSpendCard.tsx';
import { useGuaranteedWrites, type PatchOptions, type WriteTarget } from './guaranteed/useGuaranteedWrites.ts';

type CreateMode = 'sku' | 'org';

export function isDirectOrgId(value: string): boolean {
  return /^org_[A-Za-z0-9_-]+$/.test(value.trim());
}

export function GuaranteedServicePage(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const list = useGuaranteedServiceList();
  const skus = useGuaranteedServiceTextSkus();
  const customersQuery = useQuotaCustomers({ staleTime: GUARANTEED_CUSTOMERS_STALE_MS });
  const writes = useGuaranteedWrites();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const customers = customersQuery.data?.customers ?? [];
  const byId = useMemo(() => new Map(customers.map((row) => [row.org_id, row])), [customers]);
  const [createMode, setCreateMode] = useState<CreateMode | null>(null);
  const [orgQuery, setOrgQuery] = useState('');
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgOpen, setOrgOpen] = useState(false);
  const orgAnchorRef = useRef<HTMLDivElement | null>(null);
  const [selectedSkuIds, setSelectedSkuIds] = useState<ReadonlySet<string>>(new Set());
  const [note, setNote] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [noteEdit, setNoteEdit] = useState<{ id: string; note: string } | null>(null);
  const textSkus = skus.data?.skus ?? [];
  const directOrgId = isDirectOrgId(orgQuery) ? orgQuery.trim() : null;
  const effectiveOrgId = orgId ?? directOrgId;
  const orgMatches = customers.filter((row) => {
    const q = orgQuery.trim().toLowerCase();
    if (q.length === 0) return false;
    return row.org_name.toLowerCase().includes(q)
      || row.org_id.toLowerCase().includes(q)
      || row.email.toLowerCase().includes(q);
  }).slice(0, 20);

  function rowTarget(id: string): (WriteTarget & { readonly name: string }) | null {
    const sku = list.data?.sku.find((row) => row.id === id);
    if (sku !== undefined) return { id, scope: 'sku', name: sku.sku_name };
    const org = list.data?.org_sku.find((row) => row.id === id);
    if (org !== undefined) {
      return { id, scope: 'org_sku', name: `${byId.get(org.org_id)?.org_name ?? org.org_id} · ${org.sku_name}` };
    }
    return null;
  }

  function applyPatch(target: WriteTarget, body: PatchGuaranteedServiceRequest, options?: PatchOptions) {
    // The row itself shows 「正在写入」 / 「已保存」 / the error; the toast on
    // failure comes from the write hook (not for a drawer that shows it inline).
    return writes.patch(target, body, options);
  }

  function openCreate(mode: CreateMode): void {
    setOrgId(null);
    setOrgQuery('');
    setOrgOpen(false);
    setSelectedSkuIds(new Set());
    setNote('');
    setCreateError(null);
    setCreateMode(mode);
  }

  function saveCreate(): void {
    const skuIds = [...selectedSkuIds];
    if (skuIds.length === 0 || createMode === null) return;
    let body: CreateGuaranteedServiceRequest;
    if (createMode === 'sku') {
      body = { scope: 'sku', sku_ids: skuIds, note: note.trim() || null };
    } else {
      if (effectiveOrgId === null) return;
      body = {
        scope: 'org_sku',
        org_id: effectiveOrgId,
        sku_ids: skuIds,
        note: note.trim() || null,
      };
    }
    setCreating(true);
    setCreateError(null);
    void writes.create(body).then((outcome) => {
      setCreating(false);
      if (outcome.ok) {
        toast.success(t('guaranteed.saved'));
        setCreateMode(null);
      } else {
        setCreateError(outcome.message);
      }
    });
  }

  return (
    <div className="flex flex-col gap-4" data-testid="guaranteed-service-page">
      <PageHeader
        title={t('guaranteed.title')}
        sub={t('guaranteed.subtitle')}
        actions={(
          <>
            <Button variant="ghost" data-testid="guaranteed-create-org" onClick={() => openCreate('org')}>
              {t('guaranteed.create_org')}
            </Button>
            <Button variant="primary" data-testid="guaranteed-create" onClick={() => openCreate('sku')}>
              {t('guaranteed.create_sku')}
            </Button>
          </>
        )}
      />
      <ConfigCards
        skuRows={list.data?.sku ?? []}
        orgRows={list.data?.org_sku ?? []}
        customerName={(org) => byId.get(org)?.org_name ?? org}
        customerEmail={(org) => byId.get(org)?.email ?? ''}
        statusFor={writes.statusFor}
        removed={writes.removed}
        onPatch={applyPatch}
        onEditNote={(id, current) => {
          setNoteError(null);
          setNoteEdit({ id, note: current });
        }}
        onDelete={setDeleteId}
      />
      <LegStatusCard />
      <ProbeSpendCard />
      <EventsCard />

      <Dialog
        open={createMode !== null}
        onOpenChange={(open) => { if (!open && !creating) setCreateMode(null); }}
        title={createMode === 'org' ? t('guaranteed.create_org_title') : t('guaranteed.create_sku_title')}
        closeLabel={t('guaranteed.close')}
        testId="guaranteed-create-dialog"
        footer={(
          <>
            <Button variant="ghost" disabled={creating} onClick={() => setCreateMode(null)}>{t('guaranteed.cancel')}</Button>
            <Button
              variant="primary"
              data-testid="guaranteed-create-save"
              disabled={(createMode === 'org' && effectiveOrgId === null) || selectedSkuIds.size === 0 || creating}
              loading={creating}
              onClick={saveCreate}
            >
              {creating ? t('guaranteed.write.creating') : t('guaranteed.save')}
            </Button>
          </>
        )}
      >
        <div className="flex flex-col gap-4">
          {createMode === 'org' ? (
            <div ref={orgAnchorRef} className="relative">
              <FormField label={t('guaranteed.customer')} fieldId="guaranteed-org-input">
                <Input
                  id="guaranteed-org-input"
                  disabled={creating}
                  value={orgId !== null ? (byId.get(orgId)?.org_name ?? orgId) : orgQuery}
                  onChange={(event) => {
                    const next = event.target.value;
                    setOrgId(null);
                    setOrgQuery(next);
                    setOrgOpen(next.trim().length > 0);
                  }}
                  onFocus={() => { if (orgQuery.trim().length > 0 && orgId === null) setOrgOpen(true); }}
                  placeholder={t('guaranteed.org_placeholder')}
                  aria-label={t('guaranteed.customer')}
                  data-testid="guaranteed-org-input"
                />
              </FormField>
              {directOrgId !== null && orgId === null ? (
                <p className="m-0 mt-1 text-dense text-paper-low" data-testid="guaranteed-org-direct">
                  {t('guaranteed.org_direct')}
                </p>
              ) : null}
              <SearchDropdown open={orgOpen && orgId === null && orgQuery.trim().length > 0} anchorRef={orgAnchorRef} testId="guaranteed-org-dropdown">
                {orgMatches.map((row) => (
                  <button
                    key={row.org_id}
                    type="button"
                    className="block w-full px-3 py-2 text-left hover:bg-active"
                    onClick={() => {
                      setOrgId(row.org_id);
                      setOrgQuery(row.org_name);
                      setOrgOpen(false);
                    }}
                  >
                    <span className="block text-dense text-paper">{row.org_name}</span>
                    <span className="block font-mono text-micro text-paper-low">{row.email} · {row.org_id}</span>
                  </button>
                ))}
              </SearchDropdown>
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <SkuGroupPicker
              skus={textSkus}
              selectedIds={selectedSkuIds}
              onChange={setSelectedSkuIds}
              otherLabel={t('guaranteed.sku_group_other')}
              placeholder={t('guaranteed.sku_placeholder')}
              filterLabel={t('guaranteed.sku')}
              listTestId="guaranteed-sku-list"
              filterTestId="guaranteed-sku-filter"
            />
          </div>
          <FormField label={t('guaranteed.note')} fieldId="guaranteed-note-input">
            <Textarea
              id="guaranteed-note-input"
              disabled={creating}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('guaranteed.note_placeholder')}
              aria-label={t('guaranteed.note')}
              data-testid="guaranteed-note-input"
            />
          </FormField>
          {createError !== null ? (
            <p role="alert" className="m-0 text-dense text-bad" data-testid="guaranteed-create-error">
              {t('guaranteed.write.failed', { message: createError })}
            </p>
          ) : null}
        </div>
      </Dialog>

      <ConfirmDialog
        open={deleteId !== null}
        onOpenChange={(open) => { if (!open) setDeleteId(null); }}
        title={t('guaranteed.delete')}
        body={t('guaranteed.delete_confirm')}
        confirmLabel={t('guaranteed.delete')}
        cancelLabel={t('guaranteed.cancel')}
        closeLabel={t('guaranteed.close')}
        tone="danger"
        testId="guaranteed-delete-dialog"
        loading={deleting}
        onConfirm={() => {
          if (deleteId === null) return;
          const target = rowTarget(deleteId);
          if (target === null) {
            setDeleteId(null);
            return;
          }
          setDeleting(true);
          void writes.remove(target).then((outcome) => {
            setDeleting(false);
            // Success or failure, the row (or the removed strip) now carries
            // the outcome inline; the dialog gets out of the way.
            setDeleteId(null);
            if (outcome.ok) toast.success(t('guaranteed.deleted'));
          });
        }}
      />

      <Dialog
        open={noteEdit !== null}
        onOpenChange={(open) => { if (!open && !noteSaving) setNoteEdit(null); }}
        title={t('guaranteed.edit_note')}
        closeLabel={t('guaranteed.close')}
        testId="guaranteed-note-dialog"
        footer={(
          <>
            <Button variant="ghost" disabled={noteSaving} onClick={() => setNoteEdit(null)}>{t('guaranteed.cancel')}</Button>
            <Button
              variant="primary"
              data-testid="guaranteed-note-save"
              disabled={noteSaving}
              loading={noteSaving}
              onClick={() => {
                if (noteEdit === null) return;
                const target = rowTarget(noteEdit.id);
                if (target === null) return;
                setNoteSaving(true);
                setNoteError(null);
                void writes.patch(target, { note: noteEdit.note.trim() || null }).then((outcome) => {
                  setNoteSaving(false);
                  if (outcome.ok) setNoteEdit(null);
                  else setNoteError(outcome.message);
                });
              }}
            >
              {noteSaving ? t('guaranteed.write.saving_short') : t('guaranteed.save')}
            </Button>
          </>
        )}
      >
        <FormField label={t('guaranteed.note')} fieldId="guaranteed-note-edit">
          <Textarea
            id="guaranteed-note-edit"
            value={noteEdit?.note ?? ''}
            onChange={(event) => setNoteEdit((current) => (
              current === null ? null : { id: current.id, note: event.target.value }
            ))}
            placeholder={t('guaranteed.note_placeholder')}
            aria-label={t('guaranteed.note')}
            data-testid="guaranteed-note-edit"
            disabled={noteSaving}
          />
        </FormField>
        {noteError !== null ? (
          <p role="alert" className="m-0 mt-2 text-dense text-bad" data-testid="guaranteed-note-error">
            {t('guaranteed.write.failed', { message: noteError })}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}
